import React from 'react';
import './BetCard.css';
import { useUserTimezone, formatWithTz } from '../hooks/useUserTimezone';
import { Badge, LockedValue } from './ui';

export interface Bet {
  id: string;
  sport: string;
  event: string;
  commenceTime: string;
  marketLastUpdate?: string;
  bookmaker: string;
  market: string;
  outcome: string;
  odds: number;
  fairOdds: number | null;
  impliedProbability: number | null;
  trueWinProbability: number | null;
  vig: number | null;
  expectedValue: number | null;
  isLive: boolean;
}

interface BetCardProps {
  bet: Bet;
  showAnalytics?: boolean;
  analyticsVisibility?: 'full' | 'teaser' | 'locked';
}

const BetCard: React.FC<BetCardProps> = ({ bet, showAnalytics = true, analyticsVisibility }) => {
  const { tz } = useUserTimezone();
  const formatTime = (timeString: string) => formatWithTz(timeString, tz);
  const resolvedAnalyticsVisibility = analyticsVisibility ?? (showAnalytics ? 'full' : 'locked');

  const formatOdds = (odds: number) => {
    return odds.toFixed(2);
  };

  const toAmericanOdds = (decimalOdds: number): string => {
    if (!isFinite(decimalOdds) || decimalOdds <= 1) {
      return 'N/A';
    }
    if (decimalOdds >= 2) {
      const american = Math.round((decimalOdds - 1) * 100);
      return `+${american}`;
    }
    const american = Math.round(-100 / (decimalOdds - 1));
    return `${american}`; // negative value includes '-' sign
  };

  const formatPercentage = (value: number) => {
    return (value * 100).toFixed(2) + '%';
  };

  const getEVColor = (ev: number) => {
    // EV should be green only if >= 0.0001 (0.01%), otherwise red
    if (ev >= 0.0001) return 'positive';
    return 'negative';
  };

  return (
    <div className={`bet-card ${bet.isLive ? 'live' : ''}`}>
      <div className="bet-header">
        <Badge variant="sport" className="sport-badge">{bet.sport}</Badge>
        {bet.isLive && <Badge variant="live" className="live-badge">LIVE</Badge>}
      </div>
      
      <div className="bet-content">
        <h3 className="event-title">
          <span className="event-title-stack">
            {(() => {
              const title = bet.event;
              let away = title;
              let home = '';
              if (title.includes(' @ ')) {
                const parts = title.split(' @ ');
                away = parts[0] || title;
                home = parts[1] || '';
              } else if (title.includes(' vs ')) {
                const parts = title.split(' vs ');
                home = parts[0] || '';
                away = parts[1] || title;
              }
              return (
                <>
                  <span className="away-team">{away}</span>
                  <span className="at-symbol">@</span>
                  <span className="home-team">{home}</span>
                </>
              );
            })()}
          </span>
        </h3>
        
        <div className="bet-details">
          <div className="detail-row">
            <span className="label">Event Start:</span>
            <span className="value nowrap">{formatTime(bet.commenceTime)}</span>
          </div>
          {bet.marketLastUpdate && (
            <div className="detail-row">
              <span className="label">Odds Last Updated:</span>
              <span className="value nowrap">{formatTime(bet.marketLastUpdate)}</span>
            </div>
          )}
          <div className="detail-row">
            <span className="label">Bookmaker:</span>
            <span className="value">{bet.bookmaker}</span>
          </div>
          <div className="detail-row">
            <span className="label">Market:</span>
            <span className="value">{bet.market}</span>
          </div>
          <div className="detail-row">
            <span className="label">Outcome:</span>
            <span className="value">{bet.outcome}</span>
          </div>
        </div>

        {resolvedAnalyticsVisibility === 'full' && (
          <div className="analytics">
            <div className="analytics-grid">
              <div className="analytics-item">
                <span className="label">Odds:</span>
                <span className="value odds">{toAmericanOdds(bet.odds)} / {formatOdds(bet.odds)}</span>
              </div>
              <div className="analytics-item">
                <span className="label">Vig:</span>
                <span className="value">{bet.vig !== null ? formatPercentage(bet.vig) : 'N/A'}</span>
              </div>
              <div className="analytics-item">
                <span className="label">Win Probability:</span>
                <span className="value">{bet.trueWinProbability !== null ? formatPercentage(bet.trueWinProbability) : 'N/A'}</span>
              </div>
              <div className="analytics-item">
                <span className="label">Expected Value:</span>
                <span className={`value ev ${getEVColor(bet.expectedValue ?? 0)}`}>
                  {bet.expectedValue !== null ? formatPercentage(bet.expectedValue) : 'N/A'}
                </span>
              </div>
            </div>
          </div>
        )}

        {resolvedAnalyticsVisibility === 'teaser' && (
          <div className="analytics teaser">
            <div className="analytics-grid">
              <div className="analytics-item">
                <span className="label">Odds:</span>
                <span className="value odds">{toAmericanOdds(bet.odds)} / {formatOdds(bet.odds)}</span>
              </div>
              <div className="analytics-item teaser-row">
                <span className="label">Fair Odds:</span>
                <LockedValue />
              </div>
              <div className="analytics-item teaser-row">
                <span className="label">Win Probability:</span>
                <LockedValue />
              </div>
              <div className="analytics-item teaser-row">
                <span className="label">Expected Value:</span>
                <LockedValue />
              </div>
            </div>
            <p className="analytics-teaser-note">Create an account to reveal fair odds, win probability, and EV.</p>
          </div>
        )}

        {resolvedAnalyticsVisibility === 'locked' && (
          <div className="analytics-locked">
            <div className="lock-icon">🔒</div>
            <p><LockedValue text="Subscribe to unlock advanced analytics" /></p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BetCard; 